import { KnownId } from "@fairfleet/geotab";
import { LiveDataRow } from "../models/LiveDataRow";
import { normalizeUnitOfMeasure } from "./checklistValues";
import { tellTales } from "@/constants/TellTales";

/**
 * Recognising a diagnostic means looking at both its id and its name.
 *
 * The id is stable but opaque, and a database can carry custom diagnostics whose ids say
 * nothing; the name is descriptive but arrives in the language the database is set up in, which
 * is why the patterns below match German and English alike.
 */
export function haystack(row: LiveDataRow): string {
  return `${row.diagnosticId} ${row.diagnosticName}`;
}

/**
 * One of Geotab's own enum ids, as opposed to an id a database generated for itself.
 *
 * Geotab's read as words and begin with their type: `DiagnosticCanProtocolId`. A database's own
 * are random and begin with a lower-case letter: `aTWDkdT3qWEy_M_tENiZcdg`.
 */
const ENUM_ID = /^[A-Z][A-Za-z]*Id$/;

/**
 * The reading's text with the words of a PascalCase name separated.
 *
 * Short names like "CAN" and "FMS" have to be matched on word boundaries, or "cancel" and
 * "confirms" answer the check. But an id writes its words without separators, so the boundaries
 * have to be put back first: "DiagnosticCanProtocolId" becomes "Diagnostic Can Protocol Id".
 *
 * A database's own ids are left out entirely. Splitting one of those at its case changes turns
 * random characters into words -- `…xCanY…` becomes `… x Can Y …` -- and a vehicle would gain a
 * CAN protocol, and with it a passing status, by pure chance in an identifier.
 */
function spacedHaystack(row: LiveDataRow): string {
  const id = ENUM_ID.test(row.diagnosticId) ? spacedWords(row.diagnosticId) : "";
  return `${id} ${spacedWords(row.diagnosticName)}`;
}

function spacedWords(text: string): string {
  return text.replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2").replace(/([a-z0-9])([A-Z])/g, "$1 $2");
}

const TELL_TALE_IDS = new Set(tellTales.map(tellTale => tellTale.id));

/**
 * A dashboard warning light: 0 = off, 1 = red, 2 = yellow, 3 = information, 7 = not available.
 *
 * Its name carries the system it warns about -- "TellTale – AdBlue tampering", "TellTale – Fuel
 * level" -- so it answers the name patterns below, and a lit light read as an AdBlue level of 1 %.
 * Recognised by the list the diagnostic monitor shows, and by the name for any light not on it.
 */
export function isTellTaleRow(row: LiveDataRow): boolean {
  return TELL_TALE_IDS.has(row.diagnosticId) || /^\s*tell\s*tale/i.test(row.diagnosticName);
}

export function isCanRow(row: LiveDataRow): boolean {
  return /\bcan\b/i.test(spacedHaystack(row));
}

const RAW_ODOMETER = /raw[\s_-]*odometer|rohkilometerstand/i;
const ODOMETER = /odometer|mileage|kilometerstand/i;
/**
 * Readings that mention the odometer without being one: trip counters, adjustments, faults.
 *
 * "Trip distance with tracked fuel source (1. Odometer, 2. Gps, 3. Road speed)" reports a 3 for
 * a vehicle counting by road speed, and was read as an odometer of 3 km.
 */
const NOT_AN_ODOMETER =
  /trip|fahrt|adjust|anpassung|service|invalid|ung[uü]ltig|fault|fehler|soot|dpf|third[\s_-]*party|source|quelle/i;

export function isRawOdometerRow(row: LiveDataRow): boolean {
  const text = haystack(row);
  return RAW_ODOMETER.test(text) && !NOT_AN_ODOMETER.test(text);
}

/**
 * The odometer the vehicle itself reports, never the raw one.
 *
 * Nearly every vehicle reports both, they are not the same number, and the raw one carries the
 * word "odometer" in its name — so it has to be excluded explicitly or it answers both checks.
 */
export function isOdometerRow(row: LiveDataRow): boolean {
  const text = haystack(row);
  return ODOMETER.test(text) && !RAW_ODOMETER.test(text) && !NOT_AN_ODOMETER.test(text);
}

/**
 * Geotab's odometer adjustment. Its real readings (with an id) are the figures someone entered by
 * hand, for 669 regiobus the 54,946 km set at installation; between them Geotab interpolates the
 * odometer it keeps from GPS, which MyGeotab shows as "Kilometerstand (verwendet jetzt GPS)".
 */
export function isOdometerAdjustmentRow(row: LiveDataRow): boolean {
  return row.diagnosticId === KnownId.DiagnosticOdometerAdjustmentId;
}

/** Geotab's interpolated GPS odometer: an adjustment row it made up, so without an id. */
export function isGpsOdometerRow(row: LiveDataRow): boolean {
  return isOdometerAdjustmentRow(row) && !row.id;
}

/** An odometer someone entered by hand: a real adjustment row, with an id. */
export function isManualOdometerRow(row: LiveDataRow): boolean {
  return isOdometerAdjustmentRow(row) && !!row.id;
}

/**
 * The two tachograph lines, addressed by the short names an installer knows them by.
 *
 * Matched on the name and on word boundaries, never on the id: Geotab ids are random strings and
 * "d8" turns up inside them by chance often enough that an unrelated reading would answer the
 * check -- with a number that looks like a real tachograph value.
 */
export function isTachoCanCRow(row: LiveDataRow): boolean {
  return /\bcanc\b/i.test(row.diagnosticName);
}

export function isTachoD8Row(row: LiveDataRow): boolean {
  return /\bd8\b/i.test(row.diagnosticName);
}

/**
 * Geotab's GO diagnostic "Tachograph features: Remote Download (RDL) (1. Active)", code 35705.
 *
 * Unlike the random ids above this one is Geotab's own, so it is matched as a whole id; the name
 * covers a database that carries the value under an id of its own.
 */
const TACHO_RDL_ID = "aBKJtgMu9GUyy9QP-hwjnHQ";

export function isTachoRdlRow(row: LiveDataRow): boolean {
  return (
    row.diagnosticId === TACHO_RDL_ID ||
    /remote[\s-]*download|\brdl\b/i.test(row.diagnosticName)
  );
}

function isFuelLevelRow(row: LiveDataRow): boolean {
  return /fuel.?level/i.test(haystack(row));
}

/**
 * The tank level as a percentage, and as a volume.
 *
 * Two checks off one diagnostic, told apart by the unit: a vehicle can report either or both, and
 * which ones arrive is exactly what an installer is verifying. Composed here rather than at each
 * page, so the single-vehicle checklist and the fleet-wide view cannot answer them differently.
 */
export function isFuelLevelPercentRow(row: LiveDataRow): boolean {
  return isFuelLevelRow(row) && isPercentOrUnknownUnit(row.unitOfMeasure);
}

export function isFuelLevelVolumeRow(row: LiveDataRow): boolean {
  // Geotab's own volume reading ("Kraftstoffstand (Volumen)", code 54) says neither "fuel" nor
  // "level" in German, so it is taken by id.
  return (
    row.diagnosticId === KnownId.DiagnosticFuelUnitsId ||
    (isFuelLevelRow(row) && isVolumeUnit(row.unitOfMeasure))
  );
}

/**
 * Whether a tank reading is a percentage — including when no unit was reported at all.
 *
 * A tank level without a unit is a percentage far more often than a volume, and treating the
 * unknown case as a volume instead would silently drop the percentage check for those vehicles.
 */
export function isPercentOrUnknownUnit(unitOfMeasure: string): boolean {
  // Normalised first, because Geotab reports the unit either as a word or as an enum id
  // (`UnitOfMeasurePercentageId`) and both have to count as a percentage.
  const normalized = normalizeUnitOfMeasure(unitOfMeasure).trim();
  return normalized === "" || normalized === "%";
}

const VOLUME_SYMBOLS = new Set(["l", "gal"]);

export function isVolumeUnit(unitOfMeasure: string): boolean {
  // Normalised first, like the percentage check, so both Geotab's enum id and the display name
  // are recognised by one rule instead of two patterns that can drift apart.
  return VOLUME_SYMBOLS.has(normalizeUnitOfMeasure(unitOfMeasure).trim().toLowerCase());
}

/**
 * Geotab's own ids for values `KnownId` does not name.
 *
 * They look random but belong to Geotab's catalog, which every database shares: checked against
 * aseag, regiobus and bvg on 2026-10-27 (5215 identical entries), and each one reported by
 * vehicles in aseag or regiobus within the week before.
 */
export const GEOTAB_IDS = {
  intelligentHarness: "aTWDkdT3qWEy_M_tENiZcdg",
  fmsStandardSoftwareVersion: "agxzM9Tiyd0S0HYwhWeHDEA",
  highVoltageStateOfCharge: "a-wr5fUdd8kuQvAvhiTOR7g",
  hvBatteryCurrent: "aoceAsl774UaFnkAEklMajw",
  hvBatteryVoltage: "a4kUpYGyQ4E2uoQTp-ed0Ow",
  evRange: "aFGR6Q9guoUeiaAZI1fD6oQ",
  cellVoltageHigh: "aZLxuFTu3N0udJtHrQ0dTYQ",
  cellVoltageLow: "aoQHb3JNnU0WaPIibHlRmcQ",
  cellTemperatureHigh: "agwqfZ6T8TEWBHU_ITGMlEw",
  cellTemperatureLow: "abc1Y3KfBkkKISu6DuGj1ZA",
  tachoCanC: "aSvKc7EMepUqdJzXZRdRu1w",
  tachoD8: "aZ01Rzh1bLEy_gj91bZfagQ",
  tachoRemoteDownload: "aBKJtgMu9GUyy9QP-hwjnHQ",
};

/** Whether an id is one of Geotab's readable enum ids rather than a random-looking one. */
export function isGeotabEnumId(id: string): boolean {
  return ENUM_ID.test(id);
}

interface DiagnosticRule {
  /**
   * Geotab's own ids for this value, where it standardises one.
   *
   * Checked as whole ids rather than as substrings, and taken from `KnownId` so they cannot be
   * misspelled — there is no `DiagnosticSpeedId`, and a typed-out literal for it matches nothing
   * while looking entirely plausible.
   */
  ids?: string[];
  /**
   * How the value is named, in the languages a database may be set up in.
   *
   * The fallback for everything Geotab does not standardise, and the only handle for a database
   * that carries the value as a custom diagnostic.
   */
  pattern?: RegExp;
}

/**
 * How each checklist value is recognised.
 *
 * All of it in one place, next to the units and the special cases below, because the fleet-wide
 * view asks the same questions of the same readings and must not have to copy the rules.
 */
const RULES = {
  intelligentHarness: {
    ids: [GEOTAB_IDS.intelligentHarness],
    pattern:
      /intelligent\w*[\s-]*(?:harness|kabelbaum|kabelstrang)|(?:harness|kabelbaum|kabelstrang)\w*[\s-]*intelligent/i,
  },
  standardHarness: {
    ids: [KnownId.DiagnosticStandardHarnessDetectedId],
    pattern:
      /standard\w*[\s-]*(?:harness|kabelbaum|kabelstrang)|(?:harness|kabelbaum|kabelstrang)\w*[\s-]*standard/i,
  },
  deviceVoltage: {
    ids: [KnownId.DiagnosticGoDeviceVoltageId],
    pattern: /device.?voltage|telematik.?spannung|ger(?:ä|ae)tespannung/i,
  },
  ignition: {
    ids: [KnownId.DiagnosticIgnitionId],
    pattern: /ignition|z(?:ü|ue)ndung/i,
  },
  vehicleSpeed: {
    ids: [KnownId.DiagnosticEngineRoadSpeedId],
    pattern: /vehicle.?speed|fahrzeuggeschwindigkeit/i,
  },
  fmsStandardSoftwareVersion: {
    ids: [GEOTAB_IDS.fmsStandardSoftwareVersion],
    pattern: /fms.?standard.?software.?version|fms-standardsoftware-version/i,
  },
  engineSpeed: {
    ids: [KnownId.DiagnosticEngineSpeedId],
    pattern:
      /^(?!.*(?:adjust(?:ment)?|calibration|reader|fan|l(?:ü|ue)fter|road|vehicle|fahrzeug|stra(?:ß|ss)en))(?=.*(?:engine|motor))(?=.*(?:speed|drehzahl|rpm))/i,
  },
  defLevel: {
    ids: [KnownId.DiagnosticDieselExhaustFluidId, KnownId.DiagnosticGenericAdblueTankLevelId],
    // "urea" only as a word: "uReader", a tachograph download box, carries it too.
    pattern: /adblue|def.?level|\burea\b|harnstoff/i,
  },
  fuelConsumption: {
    ids: [KnownId.DiagnosticDeviceTotalFuelId, KnownId.DiagnosticTotalFuelUsedId],
    pattern:
      /fuel.?consum|fuel.?used|fuel.?usage|fuel.?quantity|kraftstoffverbrauch|kraftstoffmenge/i,
  },
  stateOfCharge: {
    // The second is what regiobus' buses report instead of the generic one.
    ids: [KnownId.DiagnosticStateOfChargeId, GEOTAB_IDS.highVoltageStateOfCharge],
    pattern: /state.?of.?charge|\bsoc\b|ladezustand/i,
  },
  chargingState: {
    ids: [
      KnownId.DiagnosticElectricVehicleChargingStateId,
      KnownId.DiagnosticTotalLifetimeBatteryEnergyInDuringDCChargingId,
      KnownId.DiagnosticTotalLifetimeBatteryEnergyInDuringACChargingId,
    ],
    pattern: /charging.?state|ladestatus|ladevorgang/i,
  },
  hvBatteryCurrent: {
    ids: [GEOTAB_IDS.hvBatteryCurrent],
    pattern:
      /^(?!.*(?:adjust(?:ment)?|calibration|reader|fan|l(?:ü|ue)fter))(?=.*(?:\bhv\b|high[\s-]*voltage|hochvolt|traktion|traction))(?=.*(?:battery|batterie))(?=.*(?:current|strom))/i,
  },
  hvBatteryVoltage: {
    ids: [GEOTAB_IDS.hvBatteryVoltage],
    pattern:
      /^(?!.*(?:adjust(?:ment)?|calibration|reader|fan|l(?:ü|ue)fter))(?=.*(?:\bhv\b|high[\s-]*voltage|hochvolt|traktion|traction))(?=.*(?:battery|batterie))(?=.*(?:voltage|spannung))/i,
  },
  hvBatteryPower: {
    ids: [KnownId.DiagnosticElectricVehicleBatteryPowerId],
    pattern:
      /^(?!.*(?:adjust(?:ment)?|calibration|reader|fan|l(?:ü|ue)fter))(?=.*(?:\bhv\b|high[\s-]*voltage|hochvolt|traktion|traction))(?=.*(?:battery|batterie))(?=.*(?:power|leistung))/i,
  },
  // `KnownId` names no id for the five below; Geotab's catalog has one each, and the names are
  // the fallback for a database that carries the value as its own diagnostic.
  //
  // The range goes by several names, and Geotab's own German for it is "Verbleibende Strecke des
  // Elektrofahrzeugs" -- neither "Reichweite" nor "range" appears in that at all. A bare "range"
  // or "distance" is no use either: the first matches the Bluetooth beacon's "in range", the
  // second every odometer. So each of those needs a word beside it.
  evRange: {
    ids: [GEOTAB_IDS.evRange],
    pattern:
      /(?:rest)?reichweite|(?:verbleibende|restliche)\s+strecke|reststrecke|\b(?:remaining|electric|driving|ev|battery)[\s-]*(?:range|distance)|(?:range|distance)[\s-]*(?:remaining|estimate)/i,
  },
  // "Voltage" is not the only spelling in the field: the values observed read "Battery maximum
  // cell volt", so anything starting with "volt" counts -- volt, volts, voltage alike. Geotab's
  // own German reads "Zellenspannung", which these patterns miss; its ids catch it.
  cellVoltageHigh: {
    ids: [GEOTAB_IDS.cellVoltageHigh],
    pattern:
      /(?:zellspannung|cell[\s-]*volt)[^,;]*(?:h(?:ö|oe)chst\w*|max\w*|high)|(?:h(?:ö|oe)chst\w*|max\w*|high)[\s-]*(?:zellspannung|cell[\s-]*volt)/i,
  },
  cellVoltageLow: {
    ids: [GEOTAB_IDS.cellVoltageLow],
    pattern:
      /(?:zellspannung|cell[\s-]*volt)[^,;]*(?:niedrigst\w*|min\w*|low)|(?:niedrigst\w*|min\w*|low)[\s-]*(?:zellspannung|cell[\s-]*volt)/i,
  },
  cellTemperatureHigh: {
    ids: [GEOTAB_IDS.cellTemperatureHigh],
    pattern:
      /(?:zelltemperatur|cell[\s-]*temp\w*)[^,;]*(?:h(?:ö|oe)chst\w*|max\w*|high)|(?:h(?:ö|oe)chst\w*|max\w*|high)[\s-]*(?:zelltemperatur|cell[\s-]*temp\w*)/i,
  },
  cellTemperatureLow: {
    ids: [GEOTAB_IDS.cellTemperatureLow],
    pattern:
      /(?:zelltemperatur|cell[\s-]*temp\w*)[^,;]*(?:niedrigst\w*|min\w*|low)|(?:niedrigst\w*|min\w*|low)[\s-]*(?:zelltemperatur|cell[\s-]*temp\w*)/i,
  },
} satisfies Record<string, DiagnosticRule>;

export type CheckKey = keyof typeof RULES;

/** Geotab's own id for the value `key` asks about, where Geotab standardises one. */
export function standardIdOf(key: CheckKey): string | undefined {
  const rule: DiagnosticRule = RULES[key];
  return rule.ids?.find(id => ENUM_ID.test(id));
}

/** How strongly a reading matches the value `key` asks about. */
export function matchCheckSpecificity(key: CheckKey, row: LiveDataRow): number {
  const rule: DiagnosticRule = RULES[key];

  if (isTellTaleRow(row)) {
    return 0;
  }

  if (rule.ids?.includes(row.diagnosticId) ?? false) {
    return 2;
  }

  return (rule.pattern?.test(row.diagnosticName) ?? false) ? 1 : 0;
}

/** Whether a reading is the value `key` asks about. */
export function matchesCheck(key: CheckKey, row: LiveDataRow): boolean {
  return matchCheckSpecificity(key, row) > 0;
}

/** Every value {@link matchesCheck} can be asked about. */
export const CHECK_KEYS = Object.keys(RULES) as CheckKey[];

/** Geotab's own ids among the rules, which identify a diagnostic without looking at its name. */
export const CHECK_RULE_IDS: string[] = Object.values(RULES).flatMap(
  (rule: DiagnosticRule) => rule.ids ?? [],
);
